import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { scenariosAPI, catalogAPI, simulationLevelsAPI, simulationsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  MagnifyingGlass, 
  Clock, 
  Play,
  Spinner,
  Funnel,
  Star,
  Lightning,
  Warning,
  PencilSimple,
  Eye,
  Plus,
  ToggleLeft,
  Trash,
  FileText,
  Pulse,
  ChatCircle,
  StackSimple,
  Repeat
} from '@phosphor-icons/react';
import ScenarioTypeSelectorModal from '../components/scenarios/ScenarioTypeSelectorModal';

// v10.7.0 Fase 1 — Metadatos visuales del tipo de escenario
const SCENARIO_TYPE_META = {
  clinical:    { label: 'Clínico',            color: '#0EA5E9', bg: 'bg-cyan-50 text-cyan-700 border-cyan-200',       Icon: Pulse   },
  soft_skills: { label: 'Habilidades Blandas', color: '#8B5CF6', bg: 'bg-purple-50 text-purple-700 border-purple-200',  Icon: ChatCircle },
  hybrid:      { label: 'Híbrido',            color: '#F59E0B', bg: 'bg-amber-50 text-amber-700 border-amber-200',     Icon: StackSimple },
};

export default function ScenariosPage() {
  const navigate = useNavigate();
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const [simQuota, setSimQuota] = useState(null);

  useEffect(() => {
    if (user?.role === 'alumno') {
      simulationsAPI.getMyQuota().then(r => { if (r.data?.limite != null) setSimQuota(r.data); }).catch(() => {});
    }
  }, [user?.role]);
  const [scenarios, setScenarios] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterType, setFilterType] = useState('all');   // v10.7.0 Fase 1
  const [typeSelectorOpen, setTypeSelectorOpen] = useState(false);
  const [accessStatus, setAccessStatus] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const canManageScenarios = isAdmin || isSuperAdmin;

  useEffect(() => {
    loadData();
  }, [showInactive]);

  const loadData = async () => {
    try {
      const params = canManageScenarios && showInactive ? { include_inactive: true } : {};
      const [scenariosRes, specialtiesRes, accessRes] = await Promise.all([
        scenariosAPI.getAll(params),
        catalogAPI.getSpecialties(),
        simulationLevelsAPI.getAccessStatus().catch(() => ({ data: { has_access: true } }))
      ]);
      setScenarios(scenariosRes.data);
      setSpecialties(specialtiesRes.data);
      setAccessStatus(accessRes.data);
    } catch (error) {
      toast.error('Error al cargar escenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (scenario) => {
    try {
      await scenariosAPI.toggleActive(scenario.id);
      toast.success(`Escenario ${scenario.is_active ? 'desactivado' : 'activado'}`);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado del escenario');
    }
  };

  const handleDelete = async (scenario) => {
    if (!confirm(`¿Está seguro de eliminar el escenario "${scenario.nombre}"?`)) return;
    try {
      await scenariosAPI.delete(scenario.id);
      toast.success('Escenario eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar escenario');
    }
  };

  // Separar escenario demo del resto
  const demoScenario = scenarios.find(s => s.is_demo || s.id === 'esc-demo-iam');
  
  const filteredScenarios = scenarios.filter(scenario => {
    // Excluir el demo de la lista normal (se muestra aparte)
    if (scenario.is_demo || scenario.id === 'esc-demo-iam') return false;
    
    const matchesSearch = ((scenario.nombre || '').toLowerCase().includes((searchTerm || '').toLowerCase())) ||
                         ((scenario.briefing || '').toLowerCase().includes((searchTerm || '').toLowerCase()));
    const matchesSpecialty = filterSpecialty === 'all' || scenario.especialidad === filterSpecialty;
    const matchesLevel = filterLevel === 'all' || scenario.nivel === filterLevel;
    // v10.7.0 Fase 1: filtro por tipo (retrocompat: sin tipo = clinical)
    const scenarioType = scenario.scenario_type || 'clinical';
    const matchesType = filterType === 'all' || scenarioType === filterType;
    return matchesSearch && matchesSpecialty && matchesLevel && matchesType;
  });

  const getSpecialtyColor = (specialty) => {
    const colors = {
      emergencias: 'bg-red-100 text-red-800',
      cardiologia: 'bg-pink-100 text-pink-800',
      pediatria: 'bg-blue-100 text-blue-800',
      obstetricia: 'bg-purple-100 text-purple-800',
      neurologia: 'bg-yellow-100 text-yellow-800',
      atencion_primaria: 'bg-green-100 text-green-800',
      comunicacion: 'bg-orange-100 text-orange-800',
      trauma: 'bg-red-100 text-red-800',
      medicina_interna: 'bg-indigo-100 text-indigo-800'
    };
    return colors[specialty] || 'bg-slate-100 text-slate-800';
  };

  const getSpecialtyName = (specialty) => {
    const names = {
      emergencias: 'Emergencias',
      cardiologia: 'Cardiología',
      pediatria: 'Pediatría',
      obstetricia: 'Obstetricia',
      neurologia: 'Neurología',
      atencion_primaria: 'Atención Primaria',
      comunicacion: 'Comunicación',
      trauma: 'Trauma',
      medicina_interna: 'Medicina Interna'
    };
    return names[specialty] || specialty;
  };

  const getLevelBadge = (level) => {
    const badges = {
      alta: 'bg-red-100 text-red-800 border-red-200',
      media: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      baja: 'bg-green-100 text-green-800 border-green-200'
    };
    return badges[level] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900" data-testid="scenarios-title">
              Escenarios Clínicos
            </h1>
            <p className="text-slate-500 mt-1">Seleccione un escenario para iniciar su simulación</p>
            {simQuota && (
              <div className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${simQuota.restantes <= 0 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-cyan-50 text-cyan-800 border border-cyan-200'}`} data-testid="sim-quota-banner">
                <span className="font-semibold">Simulaciones: {simQuota.usadas}/{simQuota.limite}</span>
                <span className="text-xs opacity-75">(periodo {simQuota.periodo})</span>
                {simQuota.restantes <= 0 && <span className="text-xs font-semibold">— límite alcanzado, no puedes iniciar nuevas simulaciones</span>}
              </div>
            )}
          </div>
          
          {/* Admin controls */}
          {canManageScenarios && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  id="show-inactive"
                />
                <label htmlFor="show-inactive" className="text-sm text-slate-600">
                  Mostrar inactivos
                </label>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/app/admin/protocol-import')}
                data-testid="create-from-protocol-btn"
              >
                <FileText size={18} className="mr-2" />
                Crear desde protocolo
              </Button>
              <Button 
                onClick={() => setTypeSelectorOpen(true)}
                data-testid="create-scenario-btn"
              >
                <Plus size={18} className="mr-2" />
                Nuevo Escenario
              </Button>
            </div>
          )}
        </div>

        {/* No access warning */}
        {accessStatus && !accessStatus.has_access && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Warning size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800">Sin acceso a simulaciones</h3>
                  <p className="text-amber-700 mt-1">
                    {accessStatus.message || "Su perfil no tiene niveles de simulación asignados. Contacte al administrador."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Demo Scenario Card */}
        {demoScenario && (
          <Card className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white border-0 overflow-hidden relative">
            <div className="absolute inset-0 bg-black/10" />
            <CardHeader className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-yellow-400 text-yellow-900 font-bold animate-pulse">
                  <Star size={14} weight="fill" className="mr-1" />
                  DEMO COMERCIAL
                </Badge>
                <Badge className="bg-white/20 text-white">
                  {demoScenario.duracion_estimada || '10-15 min'}
                </Badge>
              </div>
              <CardTitle className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                <Lightning size={32} weight="fill" className="text-yellow-300" />
                {demoScenario.nombre}
              </CardTitle>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <p className="text-white/90 text-base max-w-3xl">
                {demoScenario.briefing?.substring(0, 200)}...
              </p>
              
              <div className="flex flex-wrap gap-2 text-sm">
                {demoScenario.demo_highlights?.caracteristicas_demo?.slice(0, 4).map((feature, idx) => (
                  <Badge key={`demo-feature-${idx}`} variant="outline" className="bg-white/10 text-white border-white/30">
                    {feature}
                  </Badge>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button 
                  size="lg"
                  className="bg-white text-red-600 hover:bg-white/90 font-bold shadow-lg"
                  onClick={() => navigate(`/app/simulation/${demoScenario.id}`)}
                  data-testid="start-demo-btn"
                >
                  <Play size={20} weight="fill" className="mr-2" />
                  Iniciar Demo
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/50 text-white hover:bg-white/10"
                  onClick={() => navigate(`/app/scenarios/${demoScenario.id}`)}
                  data-testid="view-demo-details-btn"
                >
                  Ver Detalles del Escenario
                </Button>
                {canManageScenarios && (
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white/50 text-white hover:bg-white/10"
                    onClick={() => navigate(`/app/admin/scenarios/${demoScenario.id}/edit`)}
                    data-testid="edit-demo-btn"
                  >
                    <PencilSimple size={18} className="mr-2" />
                    Editar
                  </Button>
                )}
              </div>
              
              <p className="text-white/70 text-sm pt-2">
                Este escenario demuestra todas las capacidades de SimuClinic: motor clínico dinámico, 
                equipo automático, eventos condicionales por omisiones, panel del instructor y debriefing con IA.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar escenarios..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-3">
            <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
              <SelectTrigger className="w-[180px]" data-testid="specialty-filter">
                <Funnel size={16} className="mr-2" />
                <SelectValue placeholder="Especialidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {specialties.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-[140px]" data-testid="level-filter">
                <SelectValue placeholder="Nivel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="media">Media</SelectItem>
                <SelectItem value="baja">Baja</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]" data-testid="scenario-type-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="clinical">Clínico</SelectItem>
                <SelectItem value="soft_skills">Habilidades Blandas</SelectItem>
                <SelectItem value="hybrid">Híbrido</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Scenarios Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredScenarios.map((scenario) => (
            <Card 
              key={scenario.id} 
              className={`hover:shadow-lg transition-shadow ${!scenario.is_active ? 'opacity-60 border-dashed' : ''}`}
              data-testid={`scenario-card-${scenario.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-lg font-semibold leading-tight">
                    {scenario.nombre}
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    {!scenario.is_active && (
                      <Badge variant="outline" className="bg-slate-100 text-slate-600">
                        Inactivo
                      </Badge>
                    )}
                    <Badge className={getLevelBadge(scenario.nivel)}>
                      {scenario.nivel}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={getSpecialtyColor(scenario.especialidad)}>
                    {getSpecialtyName(scenario.especialidad)}
                  </Badge>
                  {(() => {
                    const st = scenario.scenario_type || 'clinical';
                    const meta = SCENARIO_TYPE_META[st] || SCENARIO_TYPE_META.clinical;
                    const Icon = meta.Icon;
                    return (
                      <Badge
                        variant="outline"
                        className={`gap-1 ${meta.bg}`}
                        data-testid={`scenario-type-badge-${scenario.id}`}
                      >
                        <Icon size={11} weight="fill" />
                        {meta.label}
                      </Badge>
                    );
                  })()}
                </div>
                
                <p className="text-sm text-slate-600 line-clamp-3">
                  {scenario.briefing}
                </p>

                <div className="space-y-2 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Clock size={16} />
                    <span>Tiempo máximo: {Math.floor(scenario.tiempo_maximo / 60)} minutos</span>
                  </div>
                  <div>
                    <span className="font-medium">Objetivos:</span> {scenario.objetivos?.length || 0}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-2 space-y-2">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => navigate(`/app/scenarios/${scenario.id}`)}
                      data-testid={`view-scenario-${scenario.id}`}
                    >
                      <Eye size={16} className="mr-1" />
                      Ver
                    </Button>
                    {scenario.is_active && (
                      <Button 
                        className="flex-1"
                        onClick={() => {
                          const st = scenario.scenario_type || 'clinical';
                          if (st === 'soft_skills' || st === 'hybrid') {
                            navigate(`/app/soft-skills/play/${scenario.id}`);
                          } else {
                            navigate(`/app/simulation/${scenario.id}`);
                          }
                        }}
                        data-testid={`start-scenario-${scenario.id}`}
                      >
                        <Play size={16} className="mr-1" />
                        Iniciar
                      </Button>
                    )}
                  </div>
                  {scenario.is_active && (scenario.scenario_type === 'soft_skills' || scenario.scenario_type === 'hybrid') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-amber-700 border-amber-200 hover:bg-amber-50"
                      onClick={() => navigate(`/app/soft-skills/play/${scenario.id}?mode=rehearsal&stage=E`)}
                      data-testid={`rehearsal-scenario-${scenario.id}`}
                    >
                      <Repeat size={14} className="mr-1" />
                      Modo Ensayo (3 min · Etapa E)
                    </Button>
                  )}
                  
                  {/* Admin Actions */}
                  {canManageScenarios && (
                    <div className="flex gap-2 pt-1 border-t">
                      <Button 
                        variant="ghost"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(`/app/admin/scenarios/${scenario.id}/edit`)}
                        data-testid={`edit-scenario-${scenario.id}`}
                      >
                        <PencilSimple size={16} className="mr-1" />
                        Editar
                      </Button>
                      <Button 
                        variant="ghost"
                        size="sm"
                        className={scenario.is_active ? 'text-amber-600' : 'text-green-600'}
                        onClick={() => handleToggleActive(scenario)}
                        data-testid={`toggle-scenario-${scenario.id}`}
                      >
                        <ToggleLeft size={16} className="mr-1" />
                        {scenario.is_active ? 'Desactivar' : 'Activar'}
                      </Button>
                      {isSuperAdmin && (
                        <Button 
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(scenario)}
                          data-testid={`delete-scenario-${scenario.id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredScenarios.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">No se encontraron escenarios con los filtros seleccionados.</p>
          </div>
        )}
      </div>
      <ScenarioTypeSelectorModal
        open={typeSelectorOpen}
        onClose={() => setTypeSelectorOpen(false)}
      />
    </>
  );
}
